import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useAddPaymentForm } from '../hooks/useAddPaymentForm';
import type { PayPeriod, PaymentFormValues, PaymentType } from '../types/payment';
import { formatCurrency } from '../utils/currency';
import { ContextCard } from './ContextCard';
import { ErrorState } from './ErrorState';
import { ModalFooter } from './ModalFooter';
import { PaymentFormFields } from './PaymentFormFields';
import { PaymentTypePicker } from './PaymentTypePicker';
import { ReviewSummary } from './ReviewSummary';
import { SuccessState } from './SuccessState';
import { UnavailableState } from './UnavailableState';
interface AddPaymentModalProps {
  payPeriod: PayPeriod;
  initialPaymentType?: PaymentType | null;
  initialValues?: Partial<PaymentFormValues>;
  initialStep?: 'form' | 'success';
  forceFailure?: boolean;
  unavailable?: 'missing_pay_period' | 'unsupported_model' | null;
  onClose: () => void;
}
export function AddPaymentModal({
  payPeriod,
  initialPaymentType = null,
  initialValues,
  initialStep = 'form',
  forceFailure = false,
  unavailable = null,
  onClose
}: AddPaymentModalProps) {
  const form = useAddPaymentForm({
    payPeriod,
    initialPaymentType,
    initialValues,
    forceFailure
  });
  React.useEffect(() => {
    if (initialStep === 'success') form.setStep('success');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStep]);
  const showFooter = !unavailable && (form.step === 'form' || form.step === 'review');
  return <motion.div initial={{
    opacity: 0,
    y: 12,
    scale: 0.985
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={{
    duration: 0.24,
    ease: [0.16, 1, 0.3, 1]
  }} role="dialog" aria-modal="true" aria-labelledby="add-payment-title" className="flex max-h-[88vh] w-full max-w-[720px] flex-col overflow-hidden rounded-xl bg-ink-50 shadow-modal" data-id="element-9" data-mp="fujbrpyq01dy">
      <header className="flex items-start justify-between gap-4 border-b border-ink-200 bg-white px-6 py-4" data-id="element-10" data-mp="1n5nn65gt4grg">
        <div data-id="element-11" data-mp="1i6swyigifpxb">
          <h1 id="add-payment-title" className="text-lg font-semibold text-ink-900" data-id="element-12" data-mp="1i6b2nrderls1">
            Add Payment
          </h1>
          <p className="mt-0.5 text-xs text-ink-500" data-id="element-13" data-mp="1e3mr3bxb80rd">
            Creates a payment line item on this employee pay period.
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close Add Payment" className="-mr-1 rounded-md p-1.5 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700 focus:outline-none focus:ring-4 focus:ring-ink-100" data-id="element-14" data-mp="146rvp1wc2ghb">
          <XIcon className="h-5 w-5" data-id="element-15" data-mp="uevwfniigkbi" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-16" data-mp="qgodcm0kr6x5">
        {unavailable ? <UnavailableState variant={unavailable} onClose={onClose} data-id="element-17" data-mp="lf8t2w6814ft" /> : form.step === 'success' && form.paymentType ? <SuccessState paymentType={form.paymentType} amount={form.primaryAmount} payPeriod={payPeriod} savedCount={form.savedCount} onAddAnother={form.reset} onDone={onClose} data-id="element-18" data-mp="ghz75qi1jawq" /> : form.step === 'error' ? <ErrorState onRetry={form.save} onBack={() => form.setStep('form')} data-id="element-19" data-mp="323i5m942xsiq" /> : <div className="space-y-4" data-id="element-20" data-mp="363sd5q7isqzp">
            <ContextCard payPeriod={payPeriod} data-id="element-21" data-mp="29e1jft71z7uy" />

            {form.step === 'form' ? <>
                <div className="rounded-lg border border-ink-200 bg-white px-5 py-4 shadow-card" data-id="element-22" data-mp="2ecw7panshomg">
                  <PaymentTypePicker value={form.paymentType} onChange={form.setPaymentType} data-id="element-23" data-mp="2iezmo99o3bch" />
                </div>
                <AnimatePresence initial={false} data-id="element-24" data-mp="2n7andec8f163">
                  {form.paymentType && <PaymentFormFields paymentType={form.paymentType} payPeriod={payPeriod} visibility={form.visibility} values={form.values} errors={form.errors} setValue={form.setValue} formatOnBlur={form.formatOnBlur} data-id="element-25" data-mp="1qlh2na4mot7i" />}
                </AnimatePresence>
              </> : form.paymentType && <ReviewSummary values={form.values} paymentType={form.paymentType} payPeriod={payPeriod} data-id="element-26" data-mp="1ub1i10iw6gzb" />}
          </div>}
      </div>

      {showFooter && <ModalFooter errorCount={form.errorCount} status={form.paymentType ? <span className="numeric" data-id="element-28" data-mp="1z89zt004oj01">
                Payment amount{' '}
                <span className="font-semibold text-ink-800" data-id="element-29" data-mp="9vg9whigj187">
                  {formatCurrency(form.primaryAmount)}
                </span>
              </span> : 'Select a payment type to continue'} secondaryLabel={form.step === 'review' ? 'Back' : 'Cancel'} onSecondary={form.step === 'review' ? () => form.setStep('form') : onClose} primaryLabel={form.step === 'review' ? 'Save Payment' : 'Review & Save'} primaryIcon={form.step === 'review' ? 'check' : 'arrow'} primaryDisabled={!form.paymentType} loading={form.saving} onPrimary={form.step === 'review' ? form.save : form.goToReview} data-id="element-27" data-mp="248ftsmtvehla" />}
    </motion.div>;
}